import { useEffect, useState } from "react";
import { getLenderDeals, getLenderDeal, lenderDecision } from "@/Calls/callLenders";
import { Car, CheckCircle, XCircle, HelpCircle } from "lucide-react";

interface LenderDeal {
    deal_id: number;
    deal_token: string;
    deal_status: string | null;
    deal_type: string | null;
    deal_locked: boolean;
    selected_lender_id: number | null;
    vYear: number | null;
    vMake: string | null;
    vModel: string | null;
    vVin: string | null;
    priceVehicle: string | null;
    downPayment: string | null;
    apr: string | null;
    term: string | null;
    amountDDue: string | null;
    monthlypymts: string | null;
    salesPerson1Name: string | null;
    deal_created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
    PENDING: "bg-yellow-100 text-yellow-700",
    VIEWED: "bg-blue-100 text-blue-700",
    ACCEPTED: "bg-green-100 text-green-700",
    DECLINED: "bg-red-100 text-red-700",
    COUNTERED: "bg-orange-100 text-orange-700",
};

export default function LendersDashboardPage() {
    const [deals, setDeals] = useState<LenderDeal[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<{ deal: LenderDeal; creditApp: any } | null>(null);
    const [deciding, setDeciding] = useState(false);
    const [filter, setFilter] = useState<string>("ALL");

    const load = () => {
        setLoading(true);
        getLenderDeals(filter === "ALL" ? undefined : filter)
            .then(setDeals)
            .catch(() => setDeals([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [filter]);

    const openDeal = async (deal: LenderDeal) => {
        try {
            const data = await getLenderDeal(deal.deal_id);
            setSelected(data);
        } catch { /* ignore */ }
    };

    const decide = async (dealId: number, decision: "APPROVED" | "INFO_REQUESTED" | "DECLINED") => {
        setDeciding(true);
        try {
            await lenderDecision(dealId, decision);
            setSelected(null);
            load();
        } finally {
            setDeciding(false);
        }
    };

    const submitted = deals.length;
    const approved = deals.filter(d => d.deal_status === "ACCEPTED").length;
    const pending = deals.filter(d => d.deal_status === "PENDING" || d.deal_status === "VIEWED").length;

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-light uppercase tracking-wide">Lender Dashboard</h1>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { label: "Submitted", value: submitted, icon: Car, color: "text-blue-600 bg-blue-50" },
                    { label: "Approved", value: approved, icon: CheckCircle, color: "text-green-600 bg-green-50" },
                    { label: "Pending Review", value: pending, icon: HelpCircle, color: "text-yellow-600 bg-yellow-50" },
                ].map(s => (
                    <div key={s.label} className="bg-white rounded-2xl shadow p-5 flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${s.color}`}>
                            <s.icon className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{loading ? "—" : s.value}</p>
                            <p className="text-xs text-gray-500 uppercase">{s.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter tabs */}
            <div className="flex gap-2">
                {["ALL", "PENDING", "VIEWED", "ACCEPTED", "DECLINED"].map(f => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-3 py-1 text-xs rounded-full border transition ${filter === f ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 text-gray-500 hover:border-indigo-400"}`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            {/* Deals Table */}
            <div className="bg-white rounded-2xl shadow p-6">
                <h2 className="font-semibold text-lg mb-4">Deal Queue</h2>
                {loading ? (
                    <p className="text-sm text-gray-400">Loading deals...</p>
                ) : deals.length === 0 ? (
                    <p className="text-sm text-gray-400">No deals routed to you yet.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-gray-400 border-b">
                            <tr>
                                <th className="text-left py-2">Vehicle</th>
                                <th className="text-left py-2">Type</th>
                                <th className="text-left py-2">Amount</th>
                                <th className="text-left py-2">Monthly</th>
                                <th className="text-left py-2">APR</th>
                                <th className="text-left py-2">Status</th>
                                <th className="text-left py-2">Date</th>
                                <th className="py-2" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {deals.map(d => (
                                <tr key={d.deal_id} className="hover:bg-gray-50">
                                    <td className="py-2">{[d.vYear, d.vMake, d.vModel].filter(Boolean).join(" ") || `Deal #${d.deal_id}`}</td>
                                    <td className="py-2">{d.deal_type ?? "—"}</td>
                                    <td className="py-2">{d.amountDDue ? `$${Number(d.amountDDue).toLocaleString()}` : "—"}</td>
                                    <td className="py-2">{d.monthlypymts ? `$${Number(d.monthlypymts).toLocaleString()}` : "—"}</td>
                                    <td className="py-2">{d.apr ? `${d.apr}%` : "—"}</td>
                                    <td className="py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[d.deal_status ?? "PENDING"] ?? "bg-gray-100 text-gray-600"}`}>
                                            {d.deal_status ?? "PENDING"}
                                        </span>
                                    </td>
                                    <td className="py-2 text-gray-400">{new Date(d.deal_created_at).toLocaleDateString()}</td>
                                    <td className="py-2">
                                        <button onClick={() => openDeal(d)} className="text-xs text-indigo-600 hover:underline">Review</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Deal Detail Slide-over */}
            {selected && (
                <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSelected(null)}>
                    <div className="w-full max-w-xl bg-white h-full overflow-y-auto p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-sm mb-4">← Close</button>

                        <h2 className="text-xl font-semibold mb-4">
                            {[selected.deal.vYear, selected.deal.vMake, selected.deal.vModel].filter(Boolean).join(" ") || `Deal #${selected.deal.deal_id}`}
                        </h2>

                        {/* Deal info */}
                        <div className="grid grid-cols-2 gap-3 text-sm mb-6">
                            <div><span className="text-gray-400">VIN:</span> {selected.deal.vVin ?? "—"}</div>
                            <div><span className="text-gray-400">Type:</span> {selected.deal.deal_type ?? "—"}</div>
                            <div><span className="text-gray-400">Price:</span> {selected.deal.priceVehicle ? `$${Number(selected.deal.priceVehicle).toLocaleString()}` : "—"}</div>
                            <div><span className="text-gray-400">Down:</span> {selected.deal.downPayment ? `$${Number(selected.deal.downPayment).toLocaleString()}` : "—"}</div>
                            <div><span className="text-gray-400">Amount Fin.:</span> {selected.deal.amountDDue ? `$${Number(selected.deal.amountDDue).toLocaleString()}` : "—"}</div>
                            <div><span className="text-gray-400">Monthly:</span> {selected.deal.monthlypymts ? `$${Number(selected.deal.monthlypymts).toLocaleString()}` : "—"}</div>
                            <div><span className="text-gray-400">APR:</span> {selected.deal.apr ? `${selected.deal.apr}%` : "—"}</div>
                            <div><span className="text-gray-400">Term:</span> {selected.deal.term ? `${selected.deal.term} mo` : "—"}</div>
                        </div>

                        {/* Credit App */}
                        {selected.creditApp && (
                            <>
                                <h3 className="font-semibold mb-2 text-sm uppercase text-gray-400">Applicant</h3>
                                <div className="grid grid-cols-2 gap-3 text-sm mb-6">
                                    <div><span className="text-gray-400">Name:</span> {selected.creditApp.applicant_fname} {selected.creditApp.applicant_lname}</div>
                                    <div><span className="text-gray-400">Email:</span> {selected.creditApp.applicant_email_address ?? "—"}</div>
                                    <div><span className="text-gray-400">Phone:</span> {selected.creditApp.applicant_cell_phone ?? selected.creditApp.applicant_main_phone ?? "—"}</div>
                                    <div><span className="text-gray-400">DOB:</span> {selected.creditApp.applicant_dob ?? "—"}</div>
                                    <div><span className="text-gray-400">Address:</span> {[selected.creditApp.applicant_present_address1, selected.creditApp.applicant_present_addr_city, selected.creditApp.applicant_present_addr_state].filter(Boolean).join(", ") || "—"}</div>
                                    <div><span className="text-gray-400">Employer:</span> {selected.creditApp.applicant_employer1_name ?? "—"}</div>
                                    <div><span className="text-gray-400">Position:</span> {selected.creditApp.applicant_employer1_position ?? "—"}</div>
                                    <div><span className="text-gray-400">Income:</span> {selected.creditApp.applicant_employer1_salary_bringhome ?? "—"}</div>
                                </div>
                            </>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-4">
                            <button
                                disabled={deciding}
                                onClick={() => decide(selected.deal.deal_id, "APPROVED")}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                            >
                                <CheckCircle className="h-4 w-4" /> Approve
                            </button>
                            <button
                                disabled={deciding}
                                onClick={() => decide(selected.deal.deal_id, "INFO_REQUESTED")}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 text-white rounded-xl text-sm font-medium hover:bg-yellow-600 disabled:opacity-50"
                            >
                                <HelpCircle className="h-4 w-4" /> Request Info
                            </button>
                            <button
                                disabled={deciding}
                                onClick={() => decide(selected.deal.deal_id, "DECLINED")}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                            >
                                <XCircle className="h-4 w-4" /> Decline
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
